import { useMemo, useState } from 'react'

import { formatHM } from '../lib/format'
import { local, queueOp } from '../lib/sync'
import type { Task } from '../types'
import { IconChevron, IconPlay, IconPlus } from './icons'
import { useLocal } from './useLocal'

type Tab = 'active' | 'done' | 'all'

const DONE = new Set(['done', 'cancelled'])

export default function Journal({ onOpenTask, onNewTask }: {
  onOpenTask: (id: number) => void
  onNewTask: () => void
}) {
  const { rows: tasks } = useLocal<Task>(local.tasks)
  const [tab, setTab] = useState<Tab>('active')
  const [q, setQ] = useState('')

  const running = useMemo(() => tasks.some((t) => t.running_since), [tasks])

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return tasks
      .filter((t) => {
        if (tab === 'active' && DONE.has(t.work_status)) return false
        if (tab === 'done' && !DONE.has(t.work_status)) return false
        if (!needle) return true
        return (
          t.title.toLowerCase().includes(needle) ||
          (t.client_name ?? '').toLowerCase().includes(needle)
        )
      })
      .sort((a, b) => (a.work_date < b.work_date ? 1 : a.work_date > b.work_date ? -1 : b.id - a.id))
  }, [tasks, tab, q])

  // Группируем по дате: сплошной список из сотни задач на телефоне не читается.
  const groups = useMemo(() => {
    const map = new Map<string, Task[]>()
    for (const t of shown) {
      const list = map.get(t.work_date)
      if (list) list.push(t)
      else map.set(t.work_date, [t])
    }
    return [...map.entries()]
  }, [shown])

  return (
    <>
      <div className="m-head" style={{ paddingBottom: 18 }}>
        <div className="m-head-row">
          <div className="m-head-name">Журнал</div>
        </div>
        <input
          className="m-input"
          style={{ marginTop: 14, background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(156,197,220,0.28)', color: '#fff' }}
          placeholder="Поиск по клиенту или задаче"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      <div className="m-tabs">
        {(['active', 'done', 'all'] as Tab[]).map((t) => (
          <button key={t} className={tab === t ? 'on' : ''} onClick={() => setTab(t)}>
            {t === 'active' ? 'В работе' : t === 'done' ? 'Завершённые' : 'Все'}
          </button>
        ))}
      </div>

      {groups.length === 0 && <div className="m-empty">Ничего не найдено</div>}

      {groups.map(([date, list]) => (
        <div key={date}>
          <div className="m-daterow">
            <span>{humanDate(date)}</span>
            <span className="m-sub">{formatHM(list.reduce((s, t) => s + t.tracked_seconds, 0))}</span>
          </div>
          <div className="m-list">
            {list.map((t) => (
              <Row key={t.id} task={t} anyRunning={running} onOpen={() => onOpenTask(t.id)} />
            ))}
          </div>
        </div>
      ))}

      <div style={{ height: 20 }} />

      <button className="m-fab" onClick={onNewTask} aria-label="Новая задача">
        <IconPlus />
      </button>
    </>
  )
}

function Row({ task, anyRunning, onOpen }: { task: Task; anyRunning: boolean; onOpen: () => void }) {
  const done = DONE.has(task.work_status)
  const closed = task.payment_status === 'paid' || task.payment_status === 'in_payout'
  const canStart = !done && !closed && !task.running_since && !anyRunning
  const live = !!task.running_since

  return (
    <div className="m-card" style={{ display: 'flex', alignItems: 'center', gap: 12, opacity: done ? 0.72 : 1 }}>
      <button className="m-rowbtn" onClick={onOpen}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}>
          <span className="m-sub">№ {task.id}</span>
          <span className="m-client">{task.client_name}</span>
        </div>
        <div style={{ fontSize: 14, marginTop: 3 }}>{task.title}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 5, flexWrap: 'wrap' }}>
          {live && <span className="m-chip m-chip-live">идёт</span>}
          {task.estimate !== null ? (
            <span className="m-chip m-chip-est">{Number(task.estimate)} ч</span>
          ) : (
            <span className="m-chip m-chip-none">без оценки</span>
          )}
          {task.is_support && <span className="m-chip m-chip-support">сопровождение</span>}
          {done && <span className="m-chip m-chip-done">завершено</span>}
          {task.payment_status === 'paid' && <span className="m-chip m-chip-locked">выплачено</span>}
          {task.payment_status === 'in_payout' && <span className="m-chip m-chip-payout">в ведомости</span>}
          <span className="m-sub">{formatHM(task.tracked_seconds)}</span>
        </div>
      </button>

      {canStart ? (
        <button
          className={`m-round ${task.tracked_seconds > 0 ? 'm-round-resume' : 'm-round-go'}`}
          onClick={() => queueOp('timer.start', { taskId: task.id })}
          aria-label={task.tracked_seconds > 0 ? 'Продолжить' : 'Старт'}
        >
          <IconPlay color={task.tracked_seconds > 0 ? '#a15c07' : '#fff'} />
        </button>
      ) : (
        <IconChevron color="#94a3b8" />
      )}
    </div>
  )
}

const MONTHS_G = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
]

export function humanDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`)
  if (Number.isNaN(d.getTime())) return iso
  const today = new Date()
  const same = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
  if (same(d, today)) return 'Сегодня'
  const yest = new Date(today)
  yest.setDate(today.getDate() - 1)
  if (same(d, yest)) return 'Вчера'
  const base = `${d.getDate()} ${MONTHS_G[d.getMonth()]}`
  return d.getFullYear() === today.getFullYear() ? base : `${base} ${d.getFullYear()}`
}
